// src/poker/table/Card.js
import React, { useContext } from "react";
import { suitChar, suitColor } from "./helpers";
import { ThemeContext } from "../../../context/ThemeContext";

/**
 * revealMode:
 * - "none"   : brak animacji
 * - "wipeUp" : odsłanianie od dołu (board / kładzenie karty)
 * - "flip"   : odwrócenie rewers -> awers (showdown / show cards)
 */
export default function Card({
  c,
  faceDown = false,
  size = "md",

  // reveal animation
  revealMode = "none", // "none" | "wipeUp" | "flip"
  revealMs = 1000,
  revealDelayMs = 0,
}) {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === "dark";

  // LIGHT: kremowy pomarańcz
  const lightBg =
    "linear-gradient(180deg, rgba(255, 242, 226, 0.98) 0%, rgba(255, 212, 165, 0.94) 100%)";

  // DARK: ciemno-pomarańczowy (bursztyn / miedź)
  const darkBg =
    "linear-gradient(180deg, rgba(120, 74, 18, 0.92) 0%, rgba(70, 36, 10, 0.92) 100%)";

  const baseBg = isDark ? darkBg : lightBg;

  // rewers: „nitki”
  const lineA = isDark ? "rgba(255,165,0,0.55)" : "rgba(255,165,0,0.45)";
  const lineB = isDark ? "rgba(255,165,0,0.32)" : "rgba(255,165,0,0.26)";

  const backPatternA = `repeating-linear-gradient(45deg, ${lineA} 0 3px, transparent 3px 11px)`;
  const backPatternB = `repeating-linear-gradient(-45deg, ${lineB} 0 2px, transparent 2px 11px)`;

  const dur = Math.max(0, Number(revealMs) || 0);
  const delay = Math.max(0, Number(revealDelayMs) || 0);

  const useWipe = revealMode === "wipeUp";
  const useFlip = revealMode === "flip";

  // Root: geometria + border/shadow, BEZ tła (tło idzie do osobnych warstw)
  const cardRootStyle = {
    border: isDark ? "1px solid rgba(255, 165, 0, 0.55)" : "1px solid rgba(255, 165, 0, 0.70)",
    boxShadow: isDark
      ? "0 0 0 1px rgba(255,165,0,0.12) inset, 0 0 0 2px rgba(255,165,0,0.18) inset, 0 10px 22px rgba(0,0,0,0.32)"
      : "0 0 0 1px rgba(255,165,0,0.10) inset, 0 0 0 2px rgba(255,165,0,0.16) inset, 0 10px 22px rgba(0,0,0,0.18)",
    position: "relative",
    overflow: "hidden",
  };

  const text = `${c?.r}${suitChar(c?.s)}`;
  const color = suitColor(c?.s);

  // --- WipeUp styles (board) ---
  const wipeAnim = useWipe;
  const wipeLayerAnimStyle = wipeAnim
    ? {
        clipPath: "inset(100% 0 0 0)",
        transform: "translateY(10px)",
        animation: `ptv-cardIn ${dur}ms cubic-bezier(0.2, 0.9, 0.2, 1) ${delay}ms both`,
        willChange: "clip-path, transform",
      }
    : null;

  const bgFrontStyle = {
    position: "absolute",
    inset: 0,
    backgroundImage: baseBg,
    ...(wipeLayerAnimStyle || null),
  };

  const bgBackStyle = {
    position: "absolute",
    inset: 0,
    backgroundImage: `${baseBg}, ${backPatternA}, ${backPatternB}`,
    backgroundSize: "auto, 22px 22px, 22px 22px",
    backgroundPosition: "center, center, center",
    backgroundRepeat: "no-repeat, repeat, repeat",
    backgroundBlendMode: "normal, screen, screen",
  };

  const contentStyle = {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    pointerEvents: "none",
    ...(wipeLayerAnimStyle || null),
  };

  const scanStyle =
    wipeAnim
      ? {
          position: "absolute",
          left: -24,
          right: -24,
          bottom: 0,
          height: 2,
          background: isDark ? "rgba(255,165,0,0.55)" : "rgba(255,165,0,0.45)",
          filter: "blur(0.25px)",
          opacity: 0,
          pointerEvents: "none",
          animation: `ptv-scanUp ${dur}ms cubic-bezier(0.2, 0.9, 0.2, 1) ${delay}ms both`,
        }
      : null;

  // --- Flip styles (showdown / show cards) ---
  // Flip animujemy tylko wtedy, gdy karta jest docelowo odkryta (faceDown=false).
  const flipAnim = useFlip && !faceDown;

  const flipStageStyle = flipAnim
    ? {
        position: "absolute",
        inset: 0,
        perspective: "900px",
      }
    : null;

  const flipInnerStyle = flipAnim
    ? {
        position: "absolute",
        inset: 0,
        transformStyle: "preserve-3d",
        transform: "rotateY(180deg)",
        animation: `ptv-cardFlip ${dur}ms cubic-bezier(0.22, 0.9, 0.2, 1) ${delay}ms both`,
        willChange: "transform",
      }
    : null;

  const flipFaceCommon = {
    position: "absolute",
    inset: 0,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
  };

  // UWAGA: żeby startowo było widać REWERS, a na końcu AWERS:
  // - inner startuje na rotateY(180deg) i schodzi do 0deg
  // - back face jest obrócony o 180deg, front face o 0deg
  const flipBackFaceStyle = {
    ...flipFaceCommon,
    transform: "rotateY(180deg)",
  };

  const flipFrontFaceStyle = {
    ...flipFaceCommon,
    transform: "rotateY(0deg)",
  };

  const animToken = `${dur}-${delay}-${faceDown ? "X" : text}-${revealMode}`;

  return (
    <div
      className={`poker-card-rect poker-card-rect--${size}`}
      style={cardRootStyle}
      data-anim-token={animToken}
    >
      <style>
        {`
          @keyframes ptv-cardIn {
            0%   { clip-path: inset(100% 0 0 0); transform: translateY(10px); opacity: 0.98; }
            12%  { opacity: 1; }
            100% { clip-path: inset(0 0 0 0); transform: translateY(0px); opacity: 1; }
          }
          @keyframes ptv-scanUp {
            0%   { transform: translateY(0); opacity: 0; }
            10%  { opacity: 0.85; }
            100% { transform: translateY(-140%); opacity: 0; }
          }
          @keyframes ptv-cardFlip {
            0%   { transform: rotateY(180deg) scale(0.98); }
            55%  { transform: rotateY(90deg) scale(1.00); }
            100% { transform: rotateY(0deg) scale(1.00); }
          }
        `}
      </style>

      {/* 1) Karta zakryta (rewers) */}
      {faceDown && <div className="poker-card-bg-layer" style={bgBackStyle} />}

      {/* 2) Flip (showdown / show cards): rewers -> awers */}
      {!faceDown && flipAnim && (
        <div className="poker-card-flip-stage" style={flipStageStyle}>
          <div className="poker-card-flip-inner" style={flipInnerStyle}>
            <div className="poker-card-flip-back" style={flipBackFaceStyle}>
              <div style={bgBackStyle} />
            </div>

            <div className="poker-card-flip-front" style={flipFrontFaceStyle}>
              <div style={bgFrontStyle} />
              <div style={contentStyle}>
                <div className="poker-card-rect__txt" style={{ color }}>
                  {text}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3) Normal / WipeUp (board) */}
      {!faceDown && !flipAnim && (
        <>
          <div className="poker-card-bg-layer" style={bgFrontStyle} />
          <div className="poker-card-content-layer" style={contentStyle}>
            <div className="poker-card-rect__txt" style={{ color }}>
              {text}
            </div>
          </div>
          {scanStyle && <div style={scanStyle} />}
        </>
      )}
    </div>
  );
}